import { Link as RouterLink, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';

/**
 * Confirmation page shown right after a booking request is submitted.
 *
 * Reachable via {@code navigate('/booking-submitted', { state: { ... } })}.
 * The state carries the provider name and the requested datetime so the
 * page can read them without an extra API roundtrip. If the user lands
 * here without state (e.g. types the URL directly), bounce to their
 * bookings list.
 */
export function BookingSubmittedPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const state = (location.state ?? null) as
    | { providerName?: string; preferredDateTime?: string | null; providerId?: string }
    | null;

  if (!state || !state.providerName) {
    return <Navigate to="/bookings" replace />;
  }

  const when = state.preferredDateTime
    ? new Date(state.preferredDateTime).toLocaleString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, md: 8 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            mx: 'auto',
            mb: 3,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.success.light}33 0%, ${theme.palette.success.main}33 100%)`,
            color: 'success.main',
          }}
        >
          <MarkEmailReadRoundedIcon sx={{ fontSize: 48 }} />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5 }}>
          {t('bookingSubmitted.title')}
        </Typography>

        <Typography color="text.secondary" sx={{ mb: 3, fontSize: '1.0625rem' }}>
          {t('bookingSubmitted.subtitle', { provider: state.providerName })}
        </Typography>

        {when && (
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              bgcolor: 'action.hover',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <EventAvailableRoundedIcon color="primary" />
            <Typography sx={{ fontWeight: 700 }}>{when}</Typography>
          </Box>
        )}

        <Stack spacing={2} sx={{ textAlign: 'left', mb: 4 }}>
          <Step
            icon={<MarkEmailReadRoundedIcon color="primary" />}
            title={t('bookingSubmitted.step1Title')}
            body={t('bookingSubmitted.step1Body')}
          />
          <Step
            icon={<ChatBubbleOutlineRoundedIcon color="primary" />}
            title={t('bookingSubmitted.step2Title')}
            body={t('bookingSubmitted.step2Body')}
          />
          <Step
            icon={<StorefrontRoundedIcon color="primary" />}
            title={t('bookingSubmitted.step3Title')}
            body={t('bookingSubmitted.step3Body')}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            component={RouterLink}
            to="/bookings"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
          >
            {t('bookingSubmitted.viewMyBookings')}
          </Button>
          <Button component={RouterLink} to="/" variant="text" size="large" fullWidth>
            {t('bookingSubmitted.backHome')}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

function Step({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ flexShrink: 0, mt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {body}
        </Typography>
      </Box>
    </Stack>
  );
}
